import React, { useState, useRef } from 'react';
import { FaUpload, FaFilePdf, FaEye, FaTrash, FaSpinner } from 'react-icons/fa';
import { IconType } from 'react-icons';
import { documentService } from '../../services/documentService';
import ConfirmModal from './ConfirmModal';
import './PdfUpload.css';

const Icon: React.FC<{ icon: IconType; className?: string }> = ({ icon, className }) =>
  React.createElement(icon as React.ComponentType<{ className?: string }>, { className });

interface PdfUploadProps {
  sheetId?: string;
  docType: 'contrato' | 'transferencia';
  currentFile?: string;
  label: string;
  onUploadSuccess: (objectKey: string) => void;
  onDeleteSuccess: () => void;
}

const PdfUpload: React.FC<PdfUploadProps> = ({
  sheetId,
  docType,
  currentFile,
  label,
  onUploadSuccess,
  onDeleteSuccess,
}) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!sheetId) {
    return (
      <div className="pdf-upload">
        <span className="pdf-upload-label">{label}</span>
        <span className="pdf-upload-message">Guarde la ficha primero para subir documentos</span>
      </div>
    );
  }

  const handleFileChange = () => {
    const file = fileInputRef.current?.files?.[0];
    setFileName(file ? file.name : '');
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');
    try {
      const response = await documentService.upload(sheetId, docType, file);
      onUploadSuccess(response.data.objectKey);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setFileName('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al subir el documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await documentService.delete(sheetId, docType);
      onDeleteSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar el documento');
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  const handleView = () => {
    const url = documentService.getDownloadUrl(sheetId, docType);
    const token = localStorage.getItem('token');
    fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      })
      .catch(() => setError('Error al abrir el documento'));
  };

  return (
    <div className="pdf-upload">
      <span className="pdf-upload-label">{label}</span>
      {currentFile ? (
        <div className="pdf-upload-has-file">
          <div className="pdf-upload-file-badge">
            <Icon icon={FaFilePdf} className="pdf-icon" />
            <span>PDF subido</span>
          </div>
          <div className="pdf-upload-actions">
            <button type="button" className="btn-pdf btn-view" onClick={handleView}>
              <Icon icon={FaEye} /> Ver
            </button>
            <button
              type="button"
              className="btn-pdf btn-delete"
              onClick={() => setShowConfirm(true)}
              disabled={deleting}
            >
              {deleting ? <Icon icon={FaSpinner} className="spin" /> : <Icon icon={FaTrash} />}
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </div>
      ) : (
        <div className="pdf-upload-empty">
          <input
            type="file"
            accept=".pdf"
            ref={fileInputRef}
            id={`pdf-${docType}`}
            onChange={handleFileChange}
            hidden
          />
          <label htmlFor={`pdf-${docType}`} className="btn-pdf btn-choose">
            <Icon icon={FaFilePdf} /> {fileName || 'Seleccionar PDF...'}
          </label>
          {fileName && (
            <button
              type="button"
              className="btn-pdf btn-upload"
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? <Icon icon={FaSpinner} className="spin" /> : <Icon icon={FaUpload} />}
              {uploading ? 'Subiendo...' : 'Subir'}
            </button>
          )}
        </div>
      )}
      {error && <span className="pdf-upload-error">{error}</span>}
      <ConfirmModal
        isOpen={showConfirm}
        title="Eliminar documento"
        message={`¿Está seguro de que desea eliminar el PDF de ${label.toLowerCase()}?`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDelete}
        onCancel={() => setShowConfirm(false)}
        type="danger"
      />
    </div>
  );
};

export default PdfUpload;
